import React, { useEffect, useMemo, useState } from 'react'
import { listNotifications } from '../../api/notifications.js'
import { NOTIFICATION_TYPES, NOTIFICATION_TYPE_ORDER } from '../notificationTypes.js'

const SENDER_CATEGORIES = {
  admin: { label: 'Admin', bg: '#fce7f3', fg: '#9d174d' },
  report: { label: 'Report', bg: '#ede9fe', fg: '#5b21b6' },
}

function deriveSenderCategory(n) {
  if (n.type === 'activity') return 'report'
  return 'admin'
}

export function NotificationsPage() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeType, setActiveType] = useState(null)
  const [activeSender, setActiveSender] = useState(null)

  useEffect(() => {
    let cancelled = false
    listNotifications({ history: true })
      .then((rows) => { if (!cancelled) setItems(rows) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const availableTypes = useMemo(() => {
    const seen = new Set(items.map((n) => n.type))
    return NOTIFICATION_TYPE_ORDER.filter((t) => seen.has(t))
  }, [items])

  const availableSenders = useMemo(() => {
    const seen = new Set(items.map(deriveSenderCategory))
    return Object.keys(SENDER_CATEGORIES).filter((s) => seen.has(s))
  }, [items])

  const filtered = useMemo(() => {
    let result = items
    if (activeType) result = result.filter((n) => n.type === activeType)
    if (activeSender) result = result.filter((n) => deriveSenderCategory(n) === activeSender)
    return result
  }, [items, activeType, activeSender])

  return (
    <div>
      <h2 style={styles.heading}>Notifications</h2>
      <p style={styles.subtitle}>
        Announcements, activity updates, and system notifications.
      </p>
      {items.length > 0 && (
        <div style={styles.filterGroups}>
          {availableTypes.length > 1 && (
            <div style={styles.filterGroup}>
              <span style={styles.filterLabel}>Type</span>
              <div style={styles.filters} role="group" aria-label="Filter by type">
                <FilterPill label="All" active={activeType === null} onClick={() => setActiveType(null)} />
                {availableTypes.map((t) => (
                  <FilterPill
                    key={t}
                    label={NOTIFICATION_TYPES[t].label}
                    bg={NOTIFICATION_TYPES[t].bg}
                    fg={NOTIFICATION_TYPES[t].fg}
                    active={activeType === t}
                    onClick={() => setActiveType(activeType === t ? null : t)}
                  />
                ))}
              </div>
            </div>
          )}
          {availableSenders.length > 1 && (
            <div style={styles.filterGroup}>
              <span style={styles.filterLabel}>From</span>
              <div style={styles.filters} role="group" aria-label="Filter by sender">
                <FilterPill label="All" active={activeSender === null} onClick={() => setActiveSender(null)} />
                {availableSenders.map((s) => (
                  <FilterPill
                    key={s}
                    label={SENDER_CATEGORIES[s].label}
                    bg={SENDER_CATEGORIES[s].bg}
                    fg={SENDER_CATEGORIES[s].fg}
                    active={activeSender === s}
                    onClick={() => setActiveSender(activeSender === s ? null : s)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {loading ? (
        <p style={styles.subtitle}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p style={styles.subtitle}>No notifications yet.</p>
      ) : (
        <div style={styles.list}>
          {filtered.map((n) => {
            const meta = NOTIFICATION_TYPES[n.type] || NOTIFICATION_TYPES.info
            const sender = n.createdBy
            return (
              <div key={n.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <span style={{ ...styles.pill, background: meta.bg, color: meta.fg }}>
                      {meta.label}
                    </span>
                    {sender && (
                      <span style={styles.sender}>from {sender}</span>
                    )}
                  </div>
                  <span style={styles.date}>
                    {formatDate(n.createdAt)}
                    {n.expired ? ' · Expired' : ''}
                  </span>
                </div>
                <h3 style={styles.cardTitle}>{n.title}</h3>
                <p style={styles.body}>{n.body}</p>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function formatDate(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString() } catch { return iso }
}

function FilterPill({ label, bg, fg, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.filterPill,
        background: active ? bg || '#111827' : 'transparent',
        color: active ? fg || '#fff' : 'var(--text-secondary)',
        borderColor: active ? 'transparent' : 'var(--border)',
      }}
      aria-pressed={active}
    >
      {label}
    </button>
  )
}

const styles = {
  heading: { fontSize: '18px', fontWeight: 700, margin: '0 0 4px' },
  subtitle: { fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' },
  filterGroups: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '8px' },
  filterLabel: { fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', minWidth: '32px' },
  filters: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  filterPill: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: '999px',
    border: '1px solid var(--border)',
    cursor: 'pointer',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '16px' },
  card: {
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '16px 20px',
    background: 'var(--card-bg, #fff)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pill: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '999px',
  },
  sender: { fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' },
  date: { fontSize: '11px', color: 'var(--text-secondary)' },
  cardTitle: { fontSize: '15px', fontWeight: 600, margin: '0 0 8px' },
  body: { fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 },
}
